import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode, type SVGProps } from 'react'
import { useTranslation } from 'react-i18next'
import { api, download } from '../api/http'
import type { FileEntry } from '../api/types'
import { Button } from '../components/ui/Button'
import { EmptyState } from '../components/ui/Card'
import { Row, RowMenu, SearchBox, Table, Td, Th } from '../components/ui/DataTable'
import { useToast } from '../components/ui/Toast'
import { useWorkspaces, WorkspacePicker } from '../components/useWorkspaces'
import { confirmDialog, promptDialog } from '../components/ui/Confirm'
import {
  IconChevronRight, IconClose, IconDownload, IconEdit, IconFolder, IconFolderPlus, IconTrash, IconUpload
} from '../components/icons'
import { cn } from '../lib/cn'
import { formatBytes, formatDateTime } from '../lib/format'

interface Listing { items: FileEntry[]; usedBytes: number; quotaBytes: number }

/** Document glyph in the icon set's stroke style (`icons.tsx` has no file icon yet). */
function IconFile(props: SVGProps<SVGSVGElement>) {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" {...props}>
      <path d="M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" />
      <path d="M14 3v6h6" />
    </svg>
  )
}

const IMAGES = new Set(['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'avif'])
const SHEETS = new Set(['xlsx', 'xls', 'csv', 'ods'])
const DOCS = new Set(['doc', 'docx', 'odt', 'md', 'txt', 'rtf'])

/** Extension, lower-cased; `''` for a dotfile or a name without one. */
function extOf(name: string): string {
  const i = name.lastIndexOf('.')
  return i > 0 ? name.slice(i + 1).toLowerCase() : ''
}

/** 30 px tinted square behind the type glyph: red pdf, mint sheets, accent documents, amber images. */
function tone(f: FileEntry): string {
  if (f.type === 'dir') return 'bg-cream-200 text-ink-300'
  const ext = extOf(f.name)
  if (ext === 'pdf') return 'bg-red-50 text-red-600'
  if (SHEETS.has(ext)) return 'bg-mint-50 text-mint-500'
  if (DOCS.has(ext)) return 'bg-ginger-50 text-ginger-700'
  if (IMAGES.has(ext)) return 'bg-amber-50 text-amber-700'
  return 'bg-cream-200 text-ink-300'
}

/** A left-aligned ghost row in the details pane (the kit Button would fight over the text colour). */
function AsideAction({ onClick, danger, children }: { onClick(): void; danger?: boolean; children: ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn('flex h-[30px] items-center gap-1.5 rounded-[7px] px-2.5 text-left text-[12.5px] font-medium transition-colors',
        danger ? 'text-red-600 hover:bg-red-50 hover:text-red-700' : 'text-ink-300 hover:bg-cream-200 hover:text-ink-100')}
    >
      {children}
    </button>
  )
}

export function FilesPage() {
  const { t, i18n } = useTranslation()
  const toast = useToast()
  const [dir, setDir] = useState('')
  const [ws, setWs] = useState('')
  const [query, setQuery] = useState('')
  const [selected, setSelected] = useState<string | null>(null)
  const { items: workspaces } = useWorkspaces()
  // No explicit workspace means the personal one, which the list returns first (like WorkspacePicker).
  const current = workspaces.find(w => w.id === (ws || workspaces[0]?.id)) ?? null
  const readOnly = current?.role === 'viewer'
  // Workspace query suffix for every call (personal when empty).
  const q = ws ? `&workspace=${encodeURIComponent(ws)}` : ''
  const qs = ws ? `?workspace=${encodeURIComponent(ws)}` : ''
  const [data, setData] = useState<Listing | null>(null)
  const [uploading, setUploading] = useState(false)
  const picker = useRef<HTMLInputElement>(null)

  const fail = (err: unknown) => toast((err as Error).message, 'error')
  const load = useCallback(() => api.get<Listing>(`/api/files?path=${encodeURIComponent(dir)}${q}`).then(setData).catch(fail), [dir, q])
  useEffect(() => { void load() }, [load])

  // The details pane follows one folder and one search: both reset the selection.
  useEffect(() => { setSelected(null); setQuery('') }, [dir, ws])

  const upload = async (files: FileList | null) => {
    if (!files?.length) return
    setUploading(true)
    try {
      for (const f of Array.from(files)) {
        const form = new FormData()
        form.append('file', f, f.name)
        await api.post(`/api/files/upload?path=${encodeURIComponent(dir)}${q}`, form)
      }
      toast(t('files.uploaded'), 'success')
    } catch (err) { fail(err) } finally {
      setUploading(false)
      if (picker.current) picker.current.value = ''
      void load()
    }
  }

  const newFolder = async () => {
    const name = (await promptDialog(t('files.newFolderName'), {
      title: t('files.newFolder'),
      placeholder: t('files.newFolderName'),
    }))?.trim()
    if (!name) return
    await api.post(`/api/files/mkdir${qs}`, { path: dir ? `${dir}/${name}` : name }).catch(fail)
    void load()
  }

  const rename = async (f: FileEntry) => {
    const to = (await promptDialog(t('files.renameTo'), {
      title: t('files.rename'),
      defaultValue: f.path,
    }))?.trim()
    if (!to || to === f.path) return
    await api.post(`/api/files/move${qs}`, { from: f.path, to }).catch(fail)
    setSelected(null)
    void load()
  }

  const remove = async (f: FileEntry) => {
    if (!await confirmDialog(t('files.confirmDelete', { name: f.name }))) return
    await api.del(`/api/files?path=${encodeURIComponent(f.path)}${q}`).catch(fail)
    setSelected(null)
    void load()
  }

  const save = (f: FileEntry) => void download(`/api/files/download?path=${encodeURIComponent(f.path)}${q}`, f.name).catch(fail)

  const crumbs = dir ? dir.split('/') : []
  // Root crumb names the workspace the way the picker does (the server calls it 'Personal').
  const wsLabel = current ? (current.kind === 'personal' ? t('teams.personal') : current.name) : t('files.root')
  const pct = data && data.quotaBytes > 0 ? Math.min(100, (data.usedBytes / data.quotaBytes) * 100) : 0
  const needle = query.trim().toLowerCase()
  const items = useMemo(
    () => (data?.items ?? []).filter(f => !needle || f.name.toLowerCase().includes(needle)),
    [data, needle]
  )
  const sel = data?.items.find(f => f.path === selected) ?? null
  const modified = (f: FileEntry) => formatDateTime(f.modifiedAt, i18n.language)

  return (
    <div className="flex h-full min-h-0">
      <section
        className="min-w-0 flex-1 overflow-y-auto"
        onDragOver={e => e.preventDefault()}
        onDrop={e => { e.preventDefault(); if (!readOnly) void upload(e.dataTransfer.files) }}
      >
        <div className="mx-auto w-full max-w-[1120px] px-6 pb-12 pt-9 max-sm:px-4 max-sm:pb-24 max-sm:pt-6">
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="text-2xl font-semibold leading-8 tracking-tight text-ink-100">{t('files.title')}</h1>
            <div className="flex h-[30px] items-center rounded-full border border-cream-300 bg-cream-50 px-1">
              {workspaces.length > 1 ? (
                <WorkspacePicker items={workspaces} value={ws} onChange={setWs} inline />
              ) : (
                <span className="px-2.5 text-[12.5px] font-medium text-ink-200">{t('teams.personal')}</span>
              )}
            </div>
            <span className="flex-1" />
            {data && (
              <div className="flex items-center gap-2.5 text-[12.5px] text-ink-300">
                <span className="h-1 w-24 overflow-hidden rounded-full bg-cream-200" aria-hidden="true">
                  <span className="block h-full rounded-full bg-ginger-500" style={{ width: `${pct}%` }} />
                </span>
                <span className="font-mono tabular-nums">
                  {t('files.quota', { used: formatBytes(data.usedBytes), total: formatBytes(data.quotaBytes) })}
                </span>
              </div>
            )}
            {!readOnly && (
              <>
                <input ref={picker} type="file" multiple hidden onChange={e => void upload(e.target.files)} />
                <Button variant="secondary" size="sm" className="max-sm:h-11" onClick={() => void newFolder()}>
                  <IconFolderPlus width={15} height={15} />{t('files.newFolder')}
                </Button>
                <Button size="sm" className="max-sm:h-11" loading={uploading} onClick={() => picker.current?.click()}>
                  <IconUpload width={15} height={15} />{t('files.upload')}
                </Button>
              </>
            )}
          </div>

          <div className="mt-5 flex flex-wrap items-center gap-3">
            <nav aria-label={t('files.path')} className="flex min-w-0 flex-1 flex-wrap items-center gap-0.5 text-sm">
              {crumbs.length === 0 ? (
                <span aria-current="page" className="px-1.5 py-1 font-semibold text-ink-100">{wsLabel}</span>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={() => setDir('')}
                    className="rounded-md px-1.5 py-1 text-ink-300 transition-colors hover:bg-cream-200 hover:text-ink-100"
                  >
                    {wsLabel}
                  </button>
                  {crumbs.map((c, i) => (
                    <span key={`${c}-${i}`} className="flex min-w-0 items-center gap-0.5">
                      <IconChevronRight width={14} height={14} className="shrink-0 text-ink-500" />
                      {i === crumbs.length - 1 ? (
                        <span aria-current="page" className="truncate px-1.5 py-1 font-semibold text-ink-100">{c}</span>
                      ) : (
                        <button
                          type="button"
                          onClick={() => setDir(crumbs.slice(0, i + 1).join('/'))}
                          className="truncate rounded-md px-1.5 py-1 text-ink-300 transition-colors hover:bg-cream-200 hover:text-ink-100"
                        >
                          {c}
                        </button>
                      )}
                    </span>
                  ))}
                </>
              )}
            </nav>
            <SearchBox value={query} onChange={setQuery} placeholder={t('files.search')} className="sm:max-w-[220px]" />
          </div>

          {data && items.length === 0 ? (
            <div className="mt-4">
              <EmptyState
                icon={<IconFolder width={18} height={18} />}
                title={needle ? t('files.noResults', { query: query.trim() }) : t('files.empty')}
                body={needle ? undefined : t('files.emptyBody')}
              />
            </div>
          ) : (
            <div className="mt-4">
              <Table minWidth={640}>
                <thead>
                  <tr>
                    <Th rail />
                    <Th>{t('files.name')}</Th>
                    <Th className="w-[100px] text-right">{t('files.size')}</Th>
                    <Th className="w-[170px]">{t('files.modified')}</Th>
                    <Th className="w-14"><span className="sr-only">{t('common.actions')}</span></Th>
                  </tr>
                </thead>
                <tbody>
                  {items.map(f => {
                    const on = f.path === selected
                    return (
                      <Row
                        key={f.path}
                        onClick={() => (f.type === 'dir' ? setDir(f.path) : setSelected(f.path))}
                        className={on ? 'bg-cream-200' : undefined}
                      >
                        <Td>
                          <button
                            type="button"
                            onClick={() => (f.type === 'dir' ? setDir(f.path) : setSelected(f.path))}
                            className="flex max-w-full items-center gap-2.5 text-left"
                          >
                            <span className={cn('flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-[7px]', tone(f))}>
                              {f.type === 'dir' ? <IconFolder width={16} height={16} /> : <IconFile width={16} height={16} />}
                            </span>
                            <span className={cn('truncate text-ink-100', f.type === 'dir' && 'font-medium')} title={f.name}>{f.name}</span>
                          </button>
                        </Td>
                        <Td className="whitespace-nowrap text-right font-mono text-[13px] tabular-nums text-ink-300">
                          {f.type === 'file' ? formatBytes(f.size) : '—'}
                        </Td>
                        <Td className="whitespace-nowrap text-[13px] text-ink-300">{modified(f)}</Td>
                        <Td className="pr-3">
                          <RowMenu
                            label={t('common.more')}
                            items={[
                              ...(f.type === 'file'
                                ? [{ label: t('files.download'), icon: <IconDownload width={15} height={15} />, onSelect: () => save(f) }]
                                : []),
                              ...(!readOnly
                                ? [
                                    { label: t('files.rename'), icon: <IconEdit width={15} height={15} />, onSelect: () => void rename(f) },
                                    {
                                      label: t('common.delete'), icon: <IconTrash width={15} height={15} />, danger: true,
                                      onSelect: () => void remove(f)
                                    }
                                  ]
                                : [])
                            ]}
                          />
                        </Td>
                      </Row>
                    )
                  })}
                </tbody>
              </Table>
            </div>
          )}

          {!readOnly && (
            <div className="mt-4 flex items-center justify-center gap-2.5 rounded-xl border border-dashed border-cream-400 px-5 py-5 text-[13.5px] text-ink-300">
              <IconUpload width={18} height={18} />
              {t('files.dropHint')}
            </div>
          )}
        </div>
      </section>

      {sel && (
        <aside
          aria-label={t('files.details')}
          className="hidden w-[340px] shrink-0 overflow-y-auto border-l border-line bg-sidebar xl:block"
        >
          <div className="flex flex-col gap-5 p-5">
            <div className="flex items-center gap-2">
              <span className="flex-1 text-[13px] font-semibold text-ink-300">{t('files.details')}</span>
              <Button variant="ghost" size="icon-sm" aria-label={t('files.closeDetails')} onClick={() => setSelected(null)}>
                <IconClose width={15} height={15} />
              </Button>
            </div>

            <div className="flex h-[180px] items-center justify-center rounded-xl border border-line bg-cream-50">
              <span className={cn('flex h-16 w-16 items-center justify-center rounded-xl', tone(sel))}>
                <IconFile width={28} height={28} />
              </span>
            </div>

            <div className="flex flex-col gap-1">
              <span className="break-all text-base font-semibold text-ink-100">{sel.name}</span>
              <span className="text-[13px] text-ink-300">
                {extOf(sel.name).toUpperCase() || t('files.kindFile')} · {formatBytes(sel.size)}
              </span>
            </div>

            <Button size="sm" onClick={() => save(sel)}>
              <IconDownload width={15} height={15} />{t('files.download')}
            </Button>

            <dl className="grid grid-cols-[96px_minmax(0,1fr)] gap-x-3 gap-y-2.5 text-[13px]">
              <dt className="text-ink-300">{t('files.modified')}</dt>
              <dd className="m-0 text-ink-100">{modified(sel)}</dd>
              <dt className="text-ink-300">{t('files.location')}</dt>
              <dd className="m-0 break-all font-mono text-[12px] text-ink-300">{sel.path}</dd>
            </dl>

            {!readOnly && (
              <div className="flex flex-col gap-0.5 border-t border-line pt-3">
                <AsideAction onClick={() => void rename(sel)}>
                  <IconEdit width={15} height={15} />{t('files.rename')}
                </AsideAction>
                <AsideAction danger onClick={() => void remove(sel)}>
                  <IconTrash width={15} height={15} />{t('common.delete')}
                </AsideAction>
              </div>
            )}
          </div>
        </aside>
      )}
    </div>
  )
}
